/**
 * Helpers for the Inspector's "plain text" editing view (ADR-002 Phase B).
 *
 * The slide's source of truth is its HTML (``titleHtml`` / ``bodyHtml``),
 * rendered verbatim by ``deck_io.renderer``. To let editors work with the
 * *text* rather than raw markup — without destroying the surrounding
 * structure, attributes, images, or charts — we:
 *
 *   1. parse the HTML into a DOM (same ``DOMParser`` the codebase already
 *      uses in ``lib/threadMetadata``; jsdom provides it under test),
 *   2. walk it for copy-bearing **text nodes** (plus empty copy elements),
 *      exposing each as one labelled, editable field,
 *   3. on edit, replace *only that one target's text* and re-serialise
 *      the DOM back to an HTML string.
 *
 * Because we only ever touch text content, every attribute, ``<img>``,
 * ``<figure>`` and chart placeholder round-trips untouched — we are improving
 * how text is *shown and edited*, never changing the slide schema or its
 * structure (Phase C/D own structural direct-manipulation).
 */

/** One editable text run extracted from a slide HTML string. */
export interface TextRun {
  /**
   * Stable identity for this run within the parsed document: the ordinal
   * of the text target in a depth-first walk. Stable across pure text edits,
   * so it works as a React ``key`` and keeps input focus while typing.
   */
  path: number;
  /** The text target's current text. */
  text: string;
  /** Human label derived from the nearest block-ish ancestor element. */
  label: string;
}

// Tags whose presence we surface in a run's label, most-specific first.
// Anything else falls back to a generic "Text" label.
const LABELLED_TAGS: Record<string, string> = {
  h1: "Heading",
  h2: "Subheading",
  h3: "Heading",
  h4: "Heading",
  h5: "Heading",
  h6: "Heading",
  li: "List item",
  blockquote: "Quote",
  figcaption: "Caption",
  button: "Button",
  a: "Link",
  th: "Table header",
  td: "Table cell",
  p: "Paragraph",
};

const EMPTY_COPY_TAGS = new Set([
  ...Object.keys(LABELLED_TAGS),
  "b",
  "code",
  "em",
  "i",
  "mark",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "u",
]);

function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}

/**
 * Pick the label for a text node from its ancestor chain — the closest
 * ancestor whose tag we have a friendly name for wins; otherwise "Text".
 */
function labelFor(node: Node): string {
  let el: Element | null =
    node.nodeType === 1 ? (node as Element) : node.parentElement;
  while (el) {
    const tag = el.tagName.toLowerCase();
    const friendly = LABELLED_TAGS[tag];
    if (friendly) return friendly;
    el = el.parentElement;
  }
  return "Text";
}

/**
 * Walk a parsed body for editable text targets in document order. Empty
 * copy elements stand in for text nodes that were cleared and then
 * re-parsed from serialized HTML.
 */
type TextTarget = Text | Element;

function collectTextTargets(root: ParentNode): TextTarget[] {
  const out: TextTarget[] = [];
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3 /* TEXT_NODE */) {
        out.push(child as Text);
      } else if (child.nodeType === 1 /* ELEMENT_NODE */) {
        const el = child as Element;
        const tag = el.tagName.toLowerCase();
        if (tag === "script" || tag === "style") continue;
        if (child.childNodes.length === 0 && EMPTY_COPY_TAGS.has(tag)) {
          out.push(el);
          continue;
        }
        walk(child);
      }
    }
  };
  walk(root);
  return out;
}

/**
 * Extract the editable text runs from an HTML fragment. Layout whitespace
 * is dropped from the *editable* surface but still counted, so ``path`` stays
 * aligned with {@link setTextRun}'s view of the same document.
 */
export function extractTextRuns(html: string): TextRun[] {
  if (!html) return [];
  const doc = parseHtml(html);
  const nodes = collectTextTargets(doc.body);
  const runs: TextRun[] = [];
  nodes.forEach((node, path) => {
    const text = node.nodeType === 3 ? (node as Text).data : "";
    const label = labelFor(node);
    if (text.trim() === "" && label === "Text") return;
    runs.push({ path, text, label });
  });
  return runs;
}

/**
 * Return a new HTML string with the text run at ``path`` replaced by
 * ``nextText``. Out-of-range paths return the input unchanged.
 */
export function setTextRun(
  html: string,
  path: number,
  nextText: string,
): string {
  const doc = parseHtml(html);
  const nodes = collectTextTargets(doc.body);
  const node = nodes[path];
  if (!node) return html;
  if (node.nodeType === 3) {
    (node as Text).data = nextText;
  } else {
    node.textContent = nextText;
  }
  return doc.body.innerHTML;
}

/**
 * True when a fragment has no editable text runs at all — used to decide
 * whether to show an "add a heading / paragraph in the raw view" nudge
 * rather than an empty field list (e.g. an image-only or chart-only body).
 */
export function hasNoTextRuns(html: string): boolean {
  return extractTextRuns(html).length === 0;
}
